import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { FaArrowLeft, FaCheck, FaCircleExclamation, FaFloppyDisk, FaMagnifyingGlass, FaPaperPlane } from "react-icons/fa6";
import {
  buildOwnerDraftBanquetId,
  getBanquetsForOwner,
  saveBanquetDraft,
  submitBanquetForApproval,
  uploadBanquetImage,
  uploadBanquetPackageDocument,
  uploadBanquetVideo,
} from "../services/banquetOwnerBanquetService.js";
import { getCurrentBanquetOwner, logoutBanquetOwner } from "../services/banquetOwnerAuthService.js";
import { hasGoogleRedirectPattern, importGooglePlaceDetails } from "../services/googlePlacesImportService.js";
import { canManageAllBanquets } from "../services/banquetAccessRules.js";
import { auth, db } from "../../../firebase.js";
import BanquetFormSections from "../components/BanquetFormSections.jsx";
import {
  buildBanquetFormPayload,
  computeMissingRequiredFields,
  emptyForm,
  formFromBanquet,
  formatSavedTime,
  mediaRoles,
  slugify,
  timestampToDate,
} from "../model/banquetFormModel.js";

const BUILD_VERSION = "2026-06-06T01:39:09+05:30-nogit";

const MAX_PACKAGE_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_PACKAGE_FILES = 10;

export default function BanquetOwnerDashboardPage() {
  const [authDebugUser, setAuthDebugUser] = useState(() => auth.currentUser);
  const [owner, setOwner] = useState(null);
  const [selectedBanquet, setSelectedBanquet] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saveStatus, setSaveStatus] = useState("Saved");
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [saveDebugMessage, setSaveDebugMessage] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastSavedDraftId, setLastSavedDraftId] = useState("");
  const [validationSummaryVisible, setValidationSummaryVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [googleImportInput, setGoogleImportInput] = useState("");
  const [googleImportStatus, setGoogleImportStatus] = useState("idle");
  const [googleImportError, setGoogleImportError] = useState("");
  const [saveDebugState, setSaveDebugState] = useState({
    lastSaveAttemptTime: "",
    setDocStatus: "not attempted",
    immediateGetDocExists: "not checked",
    readableError: "",
    lines: [],
    projectCheck: "",
  });
  const mediaUploadTasksRef = useRef({});
  const packageUploadTasksRef = useRef({});
  const [packagesMenuError, setPackagesMenuError] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setAuthDebugUser(firebaseUser);
    });

    return unsubscribe;
  }, []);

  const addSaveDebugLine = useCallback((line, extra = {}) => {
    setSaveDebugState((current) => ({
      ...current,
      ...extra,
      lines: [...current.lines, line].slice(-8),
    }));
  }, []);

  useEffect(() => {
    const handleDebugEvent = (event) => {
      const detail = event.detail || {};
      setSaveDebugState((current) => ({
        ...current,
        setDocStatus: detail.setDocStatus || current.setDocStatus,
        readableError: detail.error || current.readableError,
        lines: [...current.lines, detail.message || detail.setDocStatus || "setDoc event"].slice(-8),
      }));
    };
    window.addEventListener("banquet-debug-save", handleDebugEvent);
    return () => window.removeEventListener("banquet-debug-save", handleDebugEvent);
  }, []);

  useEffect(() => {
    let mounted = true;
    getCurrentBanquetOwner()
      .then(async (currentOwner) => {
        if (!currentOwner) throw new Error("No authenticated banquet owner session found.");
        const generatedBanquetId = buildOwnerDraftBanquetId(currentOwner);
        console.info("[banquet-debug][reload] owner uid", currentOwner.uid);
        console.info("[banquet-debug][reload] generated/assigned banquet id", {
          generatedBanquetId,
          assignedBanquetIds: currentOwner.assignedBanquetIds || [],
        });
        console.info("[banquet-debug][reload] exact draft doc path being read", `banquetDrafts/${generatedBanquetId}`);
        const reloadDraftSnapshot = await getDoc(doc(db, "banquetDrafts", generatedBanquetId));
        console.info("[banquet-debug][reload] getDoc exists", reloadDraftSnapshot.exists());
        console.info("[banquet-debug][reload] saved data title/name", {
          title: reloadDraftSnapshot.data()?.title,
          name: reloadDraftSnapshot.data()?.name,
          banquetName: reloadDraftSnapshot.data()?.banquetName,
        });
        const banquets = await getBanquetsForOwner({ owner: currentOwner });
        if (!mounted) return;
        const loadedBanquet = banquets[0] || null;
        const loadedSavedAt = timestampToDate(loadedBanquet?.draftUpdatedAt);
        setOwner(currentOwner);
        setSelectedBanquet(loadedBanquet);
        setForm(loadedBanquet ? formFromBanquet(loadedBanquet) : emptyForm);
        setSaveStatus("Saved");
        setIsDirty(false);
        setLastSavedAt(loadedSavedAt);
        setLastSavedDraftId(loadedSavedAt && loadedBanquet?.id ? loadedBanquet.id : "");
        setSaveDebugMessage("");
      })
      .catch((loadError) => {
        if (!mounted) return;
        setOwner(null);
        setSelectedBanquet(null);
        setError(loadError.message || "Unable to load banquet dashboard.");
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const markDirty = useCallback(() => {
    setIsDirty(true);
    setSaveStatus("Unsaved Changes");
    setSaveDebugMessage("");
    setLastSavedDraftId("");
    setError("");
    setSuccessMessage("");
  }, []);
  const patch = (section, key, value) => {
    setForm((current) => ({ ...current, [section]: { ...current[section], [key]: value } }));
    markDirty();
  };
  const patchForm = (updater) => {
    setForm((current) => (typeof updater === "function" ? updater(current) : updater));
    markDirty();
  };
  const setTop = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    markDirty();
  };

  const applyGoogleImport = useCallback((imported) => {
    patchForm((current) => ({
      ...current,
      basicDetails: {
        ...current.basicDetails,
        banquetName: imported.name || current.basicDetails.banquetName,
      },
      locationDetails: {
        ...current.locationDetails,
        city: imported.city || current.locationDetails.city,
        locality: imported.locality || current.locationDetails.locality,
        fullAddress: imported.address || current.locationDetails.fullAddress,
        pincode: imported.pincode || current.locationDetails.pincode,
        googleMapLink: imported.googleMapsUrl || imported.mapUrl || current.locationDetails.googleMapLink,
        latitude: imported.latitude !== "" && imported.latitude !== undefined && imported.latitude !== null ? String(imported.latitude) : current.locationDetails.latitude,
        longitude: imported.longitude !== "" && imported.longitude !== undefined && imported.longitude !== null ? String(imported.longitude) : current.locationDetails.longitude,
        googlePlaceId: imported.googlePlaceId || current.locationDetails.googlePlaceId,
        googleRating: imported.googleRating !== "" && imported.googleRating !== undefined && imported.googleRating !== null ? String(imported.googleRating) : current.locationDetails.googleRating,
        googleRatingsTotal:
          imported.googleRatingsTotal !== "" && imported.googleRatingsTotal !== undefined && imported.googleRatingsTotal !== null
            ? String(imported.googleRatingsTotal)
            : current.locationDetails.googleRatingsTotal,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleImport = async () => {
    const trimmedInput = googleImportInput.trim();
    if (!trimmedInput) return;
    setGoogleImportStatus("loading");
    setGoogleImportError("");
    try {
      const looksLikeUrl = /^https?:\/\//i.test(trimmedInput) || hasGoogleRedirectPattern(trimmedInput);
      const imported = await importGooglePlaceDetails(looksLikeUrl ? "" : trimmedInput, form.locationDetails.city, looksLikeUrl ? trimmedInput : "");
      applyGoogleImport(imported);
      setGoogleImportStatus("success");
    } catch (importError) {
      setGoogleImportStatus("error");
      setGoogleImportError(importError.message || "Could not import details. Please fill manually.");
    }
  };

  const missingRequiredFields = useMemo(() => computeMissingRequiredFields(form), [form]);
  const requiredMissing = useMemo(() => missingRequiredFields.map((item) => item.label), [missingRequiredFields]);

  const completion = useMemo(() => {
    const requiredTotal = 22;
    return Math.max(0, Math.round(((requiredTotal - requiredMissing.length) / requiredTotal) * 100));
  }, [requiredMissing.length]);
  const pendingMediaUploadCount = useMemo(
    () =>
      mediaRoles.reduce((count, role) => {
        const items = form.media[role] || [];
        return count + items.filter((item) => item && !item.url && item.status === "uploading").length;
      }, 0),
    [form.media]
  );
  const pendingPackageUploadCount = useMemo(
    () => (form.packagesMenu || []).filter((item) => item && !item.url && item.status === "uploading").length,
    [form.packagesMenu]
  );
  const totalPendingUploadCount = pendingMediaUploadCount + pendingPackageUploadCount;
  const hasPendingMediaUploads = totalPendingUploadCount > 0;
  const currentAuthUser = auth.currentUser || authDebugUser;
  const banquetId = selectedBanquet?.id || (owner ? buildOwnerDraftBanquetId(owner) : "");
  const sessionReadyForSave = Boolean(currentAuthUser && owner && selectedBanquet?.id);
  const previewSlug = selectedBanquet?.slug || slugify(form.basicDetails.banquetName) || banquetId;
  // Super admin always gets full edit access regardless of detailsLocked — the lock only
  // ever restricts an owner/manager/staff account's own login-based dashboard.
  const isDetailsLocked = Boolean(selectedBanquet?.detailsLocked) && !canManageAllBanquets(owner);

  const buildPayload = useCallback(
    (formOverride = form) => buildBanquetFormPayload({ form: formOverride, selectedBanquet, owner, banquetId, previewSlug }),
    [banquetId, form, owner, previewSlug, selectedBanquet]
  );

  const scrollToFirstMissing = useCallback(() => {
    const firstMissing = missingRequiredFields[0];
    const target = firstMissing ? document.getElementById(`owner-section-${firstMissing.sectionIndex}`) : null;
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      target.querySelector("input, select, textarea, button")?.focus?.({ preventScroll: true });
    }
  }, [missingRequiredFields]);

  const showMissingSummary = useCallback(() => {
    if (!missingRequiredFields.length) return false;
    setValidationSummaryVisible(true);
    return true;
  }, [missingRequiredFields.length]);

  const setMediaItem = useCallback((role, itemId, updater) => {
    setForm((current) => {
      const nextItems = current.media[role].map((item) => {
        if (item.id !== itemId) return item;
        return typeof updater === "function" ? updater(item) : { ...item, ...updater };
      });
      return { ...current, media: { ...current.media, [role]: nextItems } };
    });
  }, []);

  const startMediaUpload = useCallback((role, itemId, file) => {
    if (!file || !banquetId || !itemId) {
      return Promise.reject(new Error("Media upload cannot start because the file or banquet id is missing."));
    }

    if (mediaUploadTasksRef.current[itemId]) {
      return mediaUploadTasksRef.current[itemId];
    }

    const uploadPromise = (role === "coverPhotos" || role === "galleryPhotos"
      ? uploadBanquetImage({
          banquetId,
          file,
          imageRole: role === "coverPhotos" ? "cover" : "gallery",
          onProgress: (progress) => setMediaItem(role, itemId, { progress }),
        })
      : uploadBanquetVideo({
          banquetId,
          file,
          onProgress: (progress) => setMediaItem(role, itemId, { progress }),
        }))
      .then((url) => {
        setMediaItem(role, itemId, { url, status: "uploaded", error: "", progress: 100 });
        return url;
      })
      .catch((uploadError) => {
        console.error("Unable to upload banquet media.", uploadError);
        setMediaItem(role, itemId, { status: "failed", error: uploadError.message || "Upload failed. Retry or remove this file." });
        throw uploadError;
      })
      .finally(() => {
        delete mediaUploadTasksRef.current[itemId];
      });

    mediaUploadTasksRef.current[itemId] = uploadPromise;
    return uploadPromise;
  }, [banquetId, setMediaItem]);

  const setPackageItem = useCallback((itemId, updater) => {
    setForm((current) => ({
      ...current,
      packagesMenu: current.packagesMenu.map((item) => (item.id === itemId ? (typeof updater === "function" ? updater(item) : { ...item, ...updater }) : item)),
    }));
  }, []);

  const startPackageUpload = useCallback((itemId, file) => {
    if (!file || !banquetId || !itemId) {
      return Promise.reject(new Error("Upload cannot start because the file or banquet id is missing."));
    }

    if (packageUploadTasksRef.current[itemId]) {
      return packageUploadTasksRef.current[itemId];
    }

    const uploadPromise = uploadBanquetPackageDocument({
      banquetId,
      file,
      onProgress: (progress) => setPackageItem(itemId, { progress }),
    })
      .then((url) => {
        setPackageItem(itemId, { url, status: "uploaded", error: "", progress: 100 });
        return url;
      })
      .catch((uploadError) => {
        console.error("Unable to upload package/menu PDF.", uploadError);
        setPackageItem(itemId, { status: "failed", error: uploadError.message || "Upload failed. Retry or remove this file." });
        throw uploadError;
      })
      .finally(() => {
        delete packageUploadTasksRef.current[itemId];
      });

    packageUploadTasksRef.current[itemId] = uploadPromise;
    return uploadPromise;
  }, [banquetId, setPackageItem]);

  const addPackageFiles = useCallback((files) => {
    const existingCount = form.packagesMenu.length;
    const accepted = [];
    const errors = [];

    Array.from(files).forEach((file) => {
      if (existingCount + accepted.length >= MAX_PACKAGE_FILES) {
        errors.push(`Maximum ${MAX_PACKAGE_FILES} files allowed.`);
        return;
      }
      if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
        errors.push(`${file.name}: only PDF files are allowed.`);
        return;
      }
      if (file.size > MAX_PACKAGE_FILE_SIZE_BYTES) {
        errors.push(`${file.name}: exceeds the 10MB limit.`);
        return;
      }
      accepted.push(file);
    });

    setPackagesMenuError(errors.join(" "));
    if (!accepted.length) return;

    const newItems = accepted.map((file) => ({
      id: `pkg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      label: file.name.replace(/\.[^.]+$/, ""),
      fileName: file.name,
      url: "",
      status: "uploading",
      progress: 0,
      error: "",
    }));

    setForm((current) => ({ ...current, packagesMenu: [...current.packagesMenu, ...newItems] }));
    markDirty();
    newItems.forEach((item, index) => startPackageUpload(item.id, accepted[index]));
  }, [form.packagesMenu.length, markDirty, startPackageUpload]);

  const updatePackageLabel = useCallback((index, value) => {
    setForm((current) => ({
      ...current,
      packagesMenu: current.packagesMenu.map((item, i) => (i === index ? { ...item, label: value } : item)),
    }));
    markDirty();
  }, [markDirty]);

  const deletePackageItem = useCallback((index) => {
    setForm((current) => ({ ...current, packagesMenu: current.packagesMenu.filter((_, i) => i !== index) }));
    markDirty();
  }, [markDirty]);

  const waitForMediaUploads = useCallback(async (sourceForm) => {
    let nextForm = sourceForm;
    const pendingItems = mediaRoles.flatMap((role) =>
      (sourceForm.media[role] || [])
        .map((item, index) => ({ role, item, index }))
        .filter(({ item }) => item && !item.url && (item.file || item.status === "uploading" || item.status === "failed"))
    );

    if (!pendingItems.length) {
      return nextForm;
    }

    setSaveStatus(`Uploading media (${pendingItems.length})`);
    setSaveDebugMessage(`Saving... waiting for ${pendingItems.length} media upload${pendingItems.length === 1 ? "" : "s"}.`);

    for (const { role, item, index } of pendingItems) {
      if (item.status === "failed") {
        throw new Error(`${item.name || "Selected media"} failed to upload. Change, retry, or delete it before saving.`);
      }

      const uploadTask = mediaUploadTasksRef.current[item.id] || (item.file ? startMediaUpload(role, item.id, item.file) : null);
      if (!uploadTask) {
        throw new Error(`${item.name || "Selected media"} is not uploaded yet. Please reselect the file and try again.`);
      }

      const url = await uploadTask;
      nextForm = {
        ...nextForm,
        media: {
          ...nextForm.media,
          [role]: nextForm.media[role].map((mediaItem, mediaIndex) =>
            mediaIndex === index || mediaItem.id === item.id
              ? { ...mediaItem, url, status: "uploaded", error: "", progress: 100 }
              : mediaItem
          ),
        },
      };
    }

    setForm(nextForm);
    return nextForm;
  }, [startMediaUpload]);

  const saveDraft = useCallback(async ({ navigateAfterSave = false, silent = false } = {}) => {
    console.info("[banquet-debug][save-click]", {
      isDirty,
      selectedBanquetId: selectedBanquet?.id || "",
      banquetIdPassedToSaveDraft: banquetId,
    });
    if (!currentAuthUser || !owner || !selectedBanquet?.id || !banquetId) {
      const missingSessionMessage = "Save failed: Unable to save draft because the banquet owner session is not ready.";
      setError(missingSessionMessage);
      setSaveDebugMessage(missingSessionMessage);
      setLastSavedDraftId("");
      setSuccessMessage("");
      return null;
    }
    if (!isDirty && !silent) {
      setSaveDebugMessage("No unsaved changes to save.");
      return selectedBanquet;
    }
    setSaveDebugState({
      lastSaveAttemptTime: new Date().toLocaleString("en-IN"),
      setDocStatus: "starting",
      immediateGetDocExists: "not checked",
      readableError: "",
      lines: [
        "Save clicked",
        `Writing to banquetDrafts/${banquetId}`,
      ],
      projectCheck: "",
    });
    setIsSaving(true);
    setSaveStatus("Saving...");
    setSaveDebugMessage("Saving...");
    try {
      const formReadyToSave = await waitForMediaUploads(form);
      setSaveStatus("Saving...");
      setSaveDebugMessage("Saving... calling saveBanquetDraft and waiting for Firebase writes.");
      const payload = buildPayload(formReadyToSave);
      setSaveDebugState((current) => {
        const writeLine = `Writing to banquetDrafts/${payload.id}`;
        return {
          ...current,
          lines: current.lines.includes(writeLine) ? current.lines : [...current.lines, writeLine].slice(-8),
        };
      });
      console.info("[banquet-debug][save-click] banquetId passed to saveBanquetDraft", payload.id);
      const updated = await saveBanquetDraft({ actingOwner: owner, banquet: payload });
      const savedDraftSnapshot = await getDoc(doc(db, "banquetDrafts", banquetId));
      const readBackExists = savedDraftSnapshot.exists();
      addSaveDebugLine(`Read back exists ${readBackExists ? "true" : "false"}`, {
        immediateGetDocExists: readBackExists ? "true" : "false",
        projectCheck: readBackExists
          ? ""
          : `Read-back false. Firebase projectId=${db.app?.options?.projectId || "unknown"}, authDomain=${db.app?.options?.authDomain || "unknown"}`,
      });
      console.info("[banquet-debug][post-save-getDoc]", {
        path: `banquetDrafts/${banquetId}`,
        exists: readBackExists,
        title: savedDraftSnapshot.data()?.title,
        name: savedDraftSnapshot.data()?.name,
        banquetName: savedDraftSnapshot.data()?.banquetName,
      });
      const savedAt = new Date();
      const savedTime = formatSavedTime(savedAt);
      setSelectedBanquet(updated);
      setForm(formFromBanquet(updated));
      setSaveStatus("Saved");
      setIsDirty(false);
      setLastSavedAt(savedAt);
      setLastSavedDraftId(updated.id || banquetId);
      setSaveDebugMessage("");
      if (!silent) {
        setError("");
        setValidationSummaryVisible(false);
        setSuccessMessage(`Draft saved successfully. Last saved: ${savedTime}`);
      }
      if (navigateAfterSave) {
        window.setTimeout(() => window.location.assign("/banquets"), 900);
      }
      return updated;
    } catch (saveError) {
      console.error("Unable to save banquet draft.", saveError);
      const failureMessage = `Save failed: ${saveError.message || "Unable to save banquet draft."}`;
      try {
        const failedSaveSnapshot = banquetId ? await getDoc(doc(db, "banquetDrafts", banquetId)) : null;
        const readBackExists = failedSaveSnapshot ? failedSaveSnapshot.exists() : false;
        addSaveDebugLine(`Read back exists ${readBackExists ? "true" : "false"}`, {
          immediateGetDocExists: readBackExists ? "true" : "false",
          readableError: `${saveError.code || "no-code"}: ${saveError.message || String(saveError)}`,
          projectCheck: readBackExists
            ? ""
            : `Read-back false. Firebase projectId=${db.app?.options?.projectId || "unknown"}, authDomain=${db.app?.options?.authDomain || "unknown"}`,
        });
      } catch (readBackError) {
        addSaveDebugLine("Read back failed", {
          immediateGetDocExists: "read failed",
          readableError: `${saveError.code || "no-code"}: ${saveError.message || String(saveError)} | readBack ${readBackError.code || "no-code"}: ${readBackError.message || String(readBackError)}`,
        });
      }
      setSaveStatus("Save Failed");
      setIsDirty(true);
      setError(failureMessage);
      setSaveDebugMessage(failureMessage);
      setLastSavedDraftId("");
      setSuccessMessage("");
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [addSaveDebugLine, banquetId, buildPayload, currentAuthUser, form, isDirty, owner, selectedBanquet, waitForMediaUploads]);

  useEffect(() => {
    const beforeUnload = (event) => {
      if (isDirty || isSaving) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [isDirty, isSaving]);

  const createSelectedMediaItem = (role, index, file) => {
    const previewUrl = URL.createObjectURL(file);
    return { id: `${role}-${Date.now()}-${index}`, name: file.name, previewUrl, file, status: "uploading", error: "", progress: 0 };
  };

  const uploadAndSet = (role, index, file) => {
    if (!file) return;
    try {
      const item = createSelectedMediaItem(role, index, file);
      setForm((current) => {
        const nextItems = [...current.media[role]];
        nextItems[index] = item;
        return { ...current, media: { ...current.media, [role]: nextItems } };
      });
      markDirty();
      startMediaUpload(role, item.id, file).catch(() => {});
    } catch (selectionError) {
      console.error("Unable to prepare selected media preview.", selectionError);
      setError("Unable to preview the selected file. Please try again.");
    }
  };

  const addMedia = (role, files, max) => {
    const selectedFiles = Array.from(files || []).filter(Boolean);
    if (!selectedFiles.length) return;
    try {
      const startIndex = form.media[role].length;
      const filesToUpload = selectedFiles.slice(0, Math.max(0, max - startIndex));
      if (!filesToUpload.length) return;
      const nextItems = filesToUpload.map((file, offset) => createSelectedMediaItem(role, startIndex + offset, file));
      setForm((current) => {
        return { ...current, media: { ...current.media, [role]: [...current.media[role], ...nextItems] } };
      });
      markDirty();
      nextItems.forEach((item) => startMediaUpload(role, item.id, item.file).catch(() => {}));
    } catch (selectionError) {
      console.error("Unable to prepare selected media previews.", selectionError);
      setError("Unable to preview the selected files. Please try again.");
    }
  };
  const deleteMedia = (role, index) => {
    patch("media", role, form.media[role].filter((_, itemIndex) => itemIndex !== index));
  };
  const moveMedia = (role, index, direction) => {
    const next = [...form.media[role]];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    patch("media", role, next);
  };

  const submit = async () => {
    // Missing fields are shown as a non-blocking notice only — nothing here should
    // prevent submission. Owners can complete the rest of the profile after approval.
    showMissingSummary();
    setError("");
    setSuccessMessage("");
    setIsSaving(true);
    setSaveStatus("Saving...");
    setSaveDebugMessage("Saving... preparing submission.");
    try {
      const formReadyToSave = await waitForMediaUploads(form);
      setSaveStatus("Submitting...");
      const updated = await submitBanquetForApproval({ actingOwner: owner, banquet: buildPayload(formReadyToSave) });
      const savedAt = new Date();
      setSelectedBanquet(updated);
      setForm(formFromBanquet(updated));
      // Deliberately distinct from the plain "Saved" draft-save status — this is the
      // only submit feedback guaranteed to stay in view (sticky footer) on a long form
      // where the success banner at the top can be scrolled out of sight.
      setSaveStatus("Submitted for approval");
      setIsDirty(false);
      setLastSavedAt(savedAt);
      setLastSavedDraftId(updated.id || banquetId);
      setError("");
      setValidationSummaryVisible(false);
      setSuccessMessage("Submitted for approval.");
      setSaveDebugMessage("");
    } catch (submitError) {
      console.error("Unable to submit banquet for approval.", submitError);
      const failureMessage = `Submit failed: ${submitError.message || "Unable to submit for approval. Please try again."}`;
      setSaveStatus("Save Failed");
      setIsDirty(true);
      setError(failureMessage);
      setSuccessMessage("");
      setLastSavedDraftId("");
      setSaveDebugMessage(failureMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    window.location.assign("/banquets");
  };

  const draftButtonsDisabled = isSaving || !isDirty || !sessionReadyForSave;
  const saveButtonClass = draftButtonsDisabled
    ? "inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl bg-slate-200 px-5 py-3 text-sm font-black text-slate-500 opacity-70"
    : "inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-emerald-200 hover:bg-emerald-700";
  const saveLaterButtonClass = draftButtonsDisabled
    ? "inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-100 px-5 py-3 text-sm font-black text-slate-400 opacity-70"
    : "inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl border border-emerald-500 bg-emerald-50 px-5 py-3 text-sm font-black text-emerald-800 hover:bg-emerald-100";
  const lastSavedLabel = lastSavedAt ? `Last saved: ${formatSavedTime(lastSavedAt)}` : "";
  const saveTargetPath = banquetId ? `banquetDrafts/${banquetId}` : "banquetDrafts/(missing-id)";
  const saveEnabled = sessionReadyForSave && isDirty && !isSaving;
  const sessionDebugDetails = (
    <div className="grid gap-2 md:grid-cols-2">
      <p>auth.currentUser?.uid: {currentAuthUser?.uid || "not loaded"}</p>
      <p>auth.currentUser?.email: {currentAuthUser?.email || "not loaded"}</p>
      <p>ownerProfile loaded: {String(Boolean(owner))}</p>
      <p>selectedBanquet loaded: {String(Boolean(selectedBanquet?.id))}</p>
      <p>loading: {String(loading)}</p>
      <p>Logged in email: {owner?.email || "not loaded"}</p>
      <p>Owner uid: {owner?.uid || "not loaded"}</p>
      <p>selectedBanquet.id: {selectedBanquet?.id || "none"}</p>
      <p>Save target path: {saveTargetPath}</p>
      <p>Save enabled: {String(saveEnabled)}</p>
      <p>isDirty: {String(isDirty)}</p>
      <p>Last save attempt time: {saveDebugState.lastSaveAttemptTime || "not attempted"}</p>
      <p>setDoc status: {saveDebugState.setDocStatus}</p>
      <p>Immediate getDoc exists: {saveDebugState.immediateGetDocExists}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 p-4 text-slate-800">
        <div className="w-full max-w-4xl">
          <p className="mb-4 text-center text-sm font-black">Loading banquet owner dashboard...</p>
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-xs font-bold text-slate-900" role="status" aria-live="polite">
          <p className="mb-2 text-sm font-black text-amber-900">Temporary Save Debug</p>
          <p className="mb-2">BUILD_VERSION: {BUILD_VERSION}</p>
          {sessionDebugDetails}
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-3 py-5 pb-32 text-slate-900 sm:px-5 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-5 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <button type="button" onClick={handleBack} className="mb-3 inline-flex min-h-[40px] touch-manipulation items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 hover:bg-slate-50">
                <FaArrowLeft /> Back
              </button>
              <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-700">DecorZoom Banquet Owner</p>
              <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-950 sm:text-4xl">Banquet profile form</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">Fill with chips, dropdowns, toggles, calendars, and media previews. Owner contact fields are internal only.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href="/banquets" className="inline-flex min-h-[42px] items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-bold text-slate-700"><FaMagnifyingGlass /> Search Your Banquet</a>
              <button type="button" onClick={logoutBanquetOwner} className="min-h-[42px] rounded-xl border border-rose-200 px-4 py-2 text-sm font-bold text-rose-700">Logout</button>
            </div>
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <div className="flex items-center justify-between text-sm font-bold"><span>Profile completion</span><span className="text-emerald-700">{completion}%</span></div>
              <div className="mt-2 h-3 overflow-hidden rounded-full bg-slate-200"><div className="h-full bg-emerald-600" style={{ width: `${completion}%` }} /></div>
            </div>
            <span className={`rounded-full px-4 py-2 text-sm font-black ${saveStatus === "Saved" || saveStatus === "Submitted for approval" ? "bg-emerald-50 text-emerald-800" : saveStatus === "Save Failed" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-800"}`}>{saveStatus}</span>
          </div>
        </header>

        {error ? <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-700"><FaCircleExclamation className="mr-2 inline" />{error}</div> : null}
        {successMessage ? <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-800"><FaCheck className="mr-2 inline" />{successMessage}</div> : null}
        {saveDebugMessage || lastSavedDraftId || lastSavedLabel ? (
          <div className={`mb-5 rounded-2xl border p-4 text-sm font-bold ${saveStatus === "Save Failed" ? "border-rose-200 bg-rose-50 text-rose-800" : saveStatus === "Saved" || saveStatus === "Submitted for approval" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} role="status" aria-live="polite">
            {saveDebugMessage ? <p>{saveDebugMessage}</p> : null}
            {lastSavedDraftId ? <p>Saved Draft ID: {lastSavedDraftId}</p> : null}
            {lastSavedLabel ? <p className="mt-1"><FaCheck className="mr-2 inline" />Draft saved successfully. {lastSavedLabel}</p> : null}
          </div>
        ) : null}
        <div className="mb-5 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-xs font-bold text-slate-900" role="status" aria-live="polite">
          <p className="mb-2 text-sm font-black text-amber-900">Temporary Save Debug</p>
          <p className="mb-2">BUILD_VERSION: {BUILD_VERSION}</p>
          {sessionDebugDetails}
          <p className="mt-2">Readable error: {saveDebugState.readableError || error || "none"}</p>
          {saveDebugState.projectCheck ? <p className="mt-2 text-rose-800">Project check: {saveDebugState.projectCheck}</p> : null}
          {saveDebugState.lines.length ? (
            <div className="mt-3 rounded-xl border border-amber-200 bg-white p-3">
              {saveDebugState.lines.map((line, index) => <p key={`${line}-${index}`}>{line}</p>)}
            </div>
          ) : (
            <div className="mt-3 rounded-xl border border-amber-200 bg-white p-3">No save attempt yet.</div>
          )}
        </div>
        {isDetailsLocked ? (
          <div className="mb-5 rounded-2xl border border-slate-300 bg-slate-100 p-4 text-sm font-bold text-slate-700" role="status" aria-live="polite">
            <FaCircleExclamation className="mr-2 inline" />
            Details locked by admin — contact super admin to request changes. You can still manage your Availability Calendar below.
          </div>
        ) : null}
        {validationSummaryVisible && missingRequiredFields.length ? (
          <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" role="status" aria-live="polite">
            <p className="font-black"><FaCircleExclamation className="mr-2 inline" />Some fields are still empty — you can submit now and complete these later:</p>
            <ol className="mt-2 list-decimal space-y-1 pl-5 font-bold">
              {missingRequiredFields.map((item) => (
                <li key={`${item.sectionIndex}-${item.label}`}>{item.sectionIndex}. {item.sectionName}: {item.label}</li>
              ))}
            </ol>
            <button type="button" onClick={scrollToFirstMissing} className="mt-3 inline-flex min-h-[36px] items-center rounded-lg border border-amber-300 bg-white px-3 text-xs font-bold text-amber-900">
              Jump to first empty field
            </button>
          </div>
        ) : null}

        <main className="space-y-5">
          <BanquetFormSections
            form={form}
            patch={patch}
            patchForm={patchForm}
            setTop={setTop}
            lockEverythingExceptAvailability={isDetailsLocked}
            googleImportProps={{
              value: googleImportInput,
              onChange: setGoogleImportInput,
              onImport: handleGoogleImport,
              status: googleImportStatus,
              error: googleImportError,
            }}
            mediaProps={{
              onAddMedia: addMedia,
              onChangeMediaItem: uploadAndSet,
              onDeleteMedia: deleteMedia,
              onMoveMedia: moveMedia,
              onAddPackageFiles: addPackageFiles,
              onUpdatePackageLabel: updatePackageLabel,
              onDeletePackageItem: deletePackageItem,
              packagesMenuError,
              maxPackageFiles: MAX_PACKAGE_FILES,
            }}
          />
        </main>
      </div>

      <footer aria-busy={isSaving} className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-3 py-3 shadow-2xl backdrop-blur sm:px-5">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-black text-slate-950">{completion}% complete - {saveStatus}</p>
            <p className={`truncate text-xs font-semibold ${isDirty ? "text-emerald-700" : "text-slate-500"}`}>{isDirty ? "Unsaved changes exist." : lastSavedLabel || (requiredMissing.length ? `Missing: ${requiredMissing.slice(0, 5).join(", ")}${requiredMissing.length > 5 ? "..." : ""}` : "Required profile fields complete.")}</p>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <button type="button" disabled={draftButtonsDisabled} onClick={() => saveDraft()} className={`${saveButtonClass} disabled:cursor-not-allowed`}><FaFloppyDisk /> {isSaving ? "Saving..." : "Save & Continue"}</button>
            <button type="button" disabled={draftButtonsDisabled} onClick={() => saveDraft({ navigateAfterSave: true })} className={`${saveLaterButtonClass} disabled:cursor-not-allowed`}><FaFloppyDisk /> {isSaving ? "Saving..." : "Save & Do It Later"}</button>
            <button type="button" disabled={isSaving || hasPendingMediaUploads} onClick={submit} className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-60"><FaPaperPlane /> {isSaving ? "Submitting..." : hasPendingMediaUploads ? `Uploading (${totalPendingUploadCount}), please wait...` : "Submit for Approval"}</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
